import React, { useEffect, useMemo } from "react";
import { DataGridPro, useGridApiRef } from "@mui/x-data-grid-pro";
import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import { getCurrentMonthWeekStartDate } from '../Utilities/dateUtility';
import { formatWeekHeader } from '../Utilities/weekNumberUtils';
import './SkuBaseForecastTable.css';
import { 
    WeekToMonthMap, 
    MonthMap, 
    WeekPromoMap, 
    TableRow, 
    WeekColumn, 
    ColumnGroupingModel,
    OutputsPromotionsTableProps 
} from '../types/promotions';

const PINNED_COLUMNS = [
    { field: "channel", headerName: "Channel", width: 100 },
    { field: "account", headerName: "Customer", width: 120 },
    { field: "sku", headerName: "SKU", width: 200 },
];

const PINNED_COLUMN_FIELDS = PINNED_COLUMNS.map((column) => column.field);
const PINNED_COLUMNS_WIDTH = PINNED_COLUMNS.reduce((total, column) => total + column.width, 0);

const OutputsPromotionsTable: React.FC<OutputsPromotionsTableProps> = ({ summaryData }) => {
    const apiRef = useGridApiRef();

    const { columns, columnGroupingModel, rows, currentMonthWeekStartDate } = useMemo(() => {
        if (!summaryData || !summaryData.groupedPromotions || summaryData.groupedPromotions.length === 0) {
            return {
                columns: [],
                columnGroupingModel: [],
                rows: [],
                currentMonthWeekStartDate: null,
            };
        }

        const weekToMonthMap: WeekToMonthMap = summaryData.weekToMonthMap || {};
        const weeks = Object.keys(weekToMonthMap);

        const monthMap: MonthMap = weeks.reduce((acc: MonthMap, week: string) => {
            const month = weekToMonthMap[week];
            if (!acc[month]) acc[month] = [];
            acc[month].push(week);
            return acc;
        }, {});

        const weekColumns: WeekColumn[] = weeks.map((week: string, index: number) => ({
            field: week,
            headerName: formatWeekHeader(week, index),
            width: 120,
            valueGetter: (value: any, row: TableRow) => row.promotions?.[week] ?? null,
            colSpan: (value: any, row: TableRow, column: any) => value?.span ?? 1,
            renderCell: (params: any) => {
                const value = params.value;
                if (!value || value.span === 0) return null;
                return (
                    <Tooltip title={value.promotionDisplayText} enterDelay={300} followCursor>
                        <Box
                            sx={{
                                backgroundColor: '#1976d2',
                                color: '#fff',
                                textAlign: 'center',
                                width: '100%',
                                height: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                overflow: 'hidden',
                                whiteSpace: 'nowrap',
                                textOverflow: 'ellipsis',
                                px: 0,
                                cursor: 'default',
                            }}
                        >
                            {value.promotionDisplayText}
                        </Box>
                    </Tooltip>
                );
            },
        }));

        const fiscalStartMonth = summaryData.startMonth || 1;
        const useRetailLabels = summaryData.fiscalCalendarRuleSet === 1;

        const columnGroupingModel: ColumnGroupingModel[] = Object.entries(monthMap).map(([month, weeks]) => {
            const monthNumber = parseInt(month, 10);
            const monthName = new Date(2000, monthNumber - 1, 1).toLocaleString("default", { month: "short" }).toUpperCase();
            const fiscalPeriod = ((monthNumber - fiscalStartMonth + 12) % 12) + 1;
            const headerName = useRetailLabels ? `P${fiscalPeriod} - ${monthName}` : monthName;

            return {
                groupId: month,
                headerName,
                children: weeks.map((week: string) => ({ field: week })),
            };
        });

        const rows: TableRow[] = [];
        summaryData.groupedPromotions.forEach((channelGroup) => {
            channelGroup.accounts.forEach((accountGroup) => {
                accountGroup.skus.forEach((sku) => {
                    const weekPromoMap: WeekPromoMap = {};

                    sku.promotions.forEach((promo) => {
                        const startIndex = weeks.findIndex((w) => w === promo.weekStartDate);
                        if (startIndex === -1) return;

                        for (let i = 0; i < promo.numberOfWeeks; i++) {
                            const week = weeks[startIndex + i];
                            if (!week) break;
                            weekPromoMap[week] = i === 0
                                ? { promotionDisplayText: promo.promotionDisplayText, span: promo.numberOfWeeks }
                                : { span: 0 };
                        }
                    });

                    rows.push({
                        id: rows.length,
                        channel: channelGroup.channel,
                        account: accountGroup.account,
                        sku: sku.skuName,
                        promotions: weekPromoMap,
                    });
                });
            });
        });

        const columns = [
            ...PINNED_COLUMNS,
            ...weekColumns,
        ];

        return {
            columns,
            columnGroupingModel,
            rows,
            currentMonthWeekStartDate: getCurrentMonthWeekStartDate(weekToMonthMap),
        };
    }, [summaryData]);

    useEffect(() => {
        if (!currentMonthWeekStartDate) return;

        const scrollToCurrentMonth = window.requestAnimationFrame(() => {
            const columnPosition = apiRef.current?.getColumnPosition?.(currentMonthWeekStartDate);
            if (typeof columnPosition !== "number" || Number.isNaN(columnPosition)) return;

            apiRef.current?.scroll?.({ left: Math.max(0, columnPosition - PINNED_COLUMNS_WIDTH) });
        });

        return () => window.cancelAnimationFrame(scrollToCurrentMonth);
    }, [apiRef, currentMonthWeekStartDate]);

    if (!summaryData?.weekToMonthMap || rows.length === 0) return <p>No promotions available</p>;

    return (
        <Box sx={{ height: "100%", width: "100%" }}>
            <DataGridPro
                apiRef={apiRef}
                rows={rows}
                columns={columns}
                columnGroupingModel={columnGroupingModel}
                initialState={{
                    pinnedColumns: { left: PINNED_COLUMN_FIELDS },
                }}
                disableVirtualization
            />
        </Box>
    );
};

export default OutputsPromotionsTable;
